import React, {useState, useEffect} from 'react';
import PropTypes from "prop-types";
import {useSelector, useDispatch} from "react-redux";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import AccordionDetails from "@mui/material/AccordionDetails";
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import {removeAnswer, reorderAnswer} from "../../../actions/answerEditorActions";
import ArgoIconButtonHook from "../../common/ArgoIconButtonHook";
import ArgoReorderArrowsHook from "../../common/ArgoReorderArrowsHook";
import ArgoTemplateListDividerHook from "../../common/ArgoTemplateListDividerHook";
import ArgoExpandCollapseButtonHook from "../../common/ArgoExpandCollapseButtonHook";
import AnswerEditorDetails from "./AnswerEditorDetailsHook";
import * as metadataUtils from "../../../utilities/metadata";
import * as expander from "../../../utilities/expander";
import * as questionTypes from "../../../constants/questionTypeConstants";
import * as TEMPLATE from "../../../constants/templateConstants";
import Box from "@mui/material/Box";


function AnswerEditor(props) {

    const {
        template,
        groupTemplateMode = false,
        questionDepth,
        isLikertAnswer = false,
        displayAsLikert,
        showCustomColor = true,
        hasAnswers,
        answerOptionTemplates,
        setMetadataErrorText,
        setStateCallback: parentSetStateCallback,
        arrayLength,
        createMode
    } = props;

    const dispatch = useDispatch();

    // Redux state
    const isGroupLikertType = groupTemplateMode && isLikertAnswer;
    const questionTemplate = useSelector(state => isGroupLikertType ? null : state.questionEditor[questionDepth]?.template);

    // Local state
    const [expanderState, setExpanderState] = useState({});
    const [expandAll, setExpandAll] = useState(true);

    useEffect(() => {
        setExpanderState((currentExpander) => 
            answerOptionTemplates.reduce((nextExpander, answerOption, index) => {
                nextExpander[index] = {open: currentExpander[index]?.open ?? false, template: answerOption};
                return nextExpander;
            }, {}),
        );
    }, [answerOptionTemplates]);

    const setStateCallback = (stateObj) => {
        if (stateObj.hasOwnProperty('expander')) {
            setExpanderState(stateObj.expander);
        }
        if (stateObj.hasOwnProperty('expandAll')) {
            setExpandAll(stateObj.expandAll);
        }
    };

    const disableIfYesNoQuestionType = () => {
        if (questionTemplate === null) {
            return false; // This condition is when in group mode
        }
        let type = questionTemplate.questionType;

        return type === questionTypes.YES_NO;
    };

    const disableIfStateOrAddressQuestionType = () => {
        if (questionTemplate === null) {
            return false; // This condition is when in group mode
        }
        let type = questionTemplate.questionType;

        return type === questionTypes.STATE_ONLY || type === questionTypes.ADDRESS_FULL;
    };

    // -----------------------------------------------------------------------------------------------------------------
    // EXPANDER FUNCTIONS ----------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    const onClickIconButtonReorder = (event, fieldName, index, depth, direction) => {
        event.stopPropagation(); // Keep accordion from expand/collapse
        dispatch(reorderAnswer(groupTemplateMode, (direction === "up"), index, questionDepth));
    };

    const onClickIconButtonRemove = (event, fieldName, index) => {
        event.stopPropagation(); // Keep accordion from expand/collapse
        dispatch(removeAnswer(groupTemplateMode, index, questionDepth));
    };

    const isAccordionOpen = (answerIndex) => {
        return expanderState[answerIndex]?.open;
    };

    // -----------------------------------------------------------------------------------------------------------------
    // RENDER ----------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    return (
        Object.keys(expanderState).length > 0  ?
            <div id={"AnswerEditor-container-div"} style={{width: "100%"}}>

                    {/*// -===========================================================================================================*/}
                    {/*// -ANSWER LIST DIVIDER */}
                    {/*// -===========================================================================================================*/}

                    {hasAnswers ?
                        <ArgoTemplateListDividerHook
                            fileName="AnswerEditor"
                            parentsLabel={(displayAsLikert ? "Likert " : "") + questionTypes.getLabel[questionTemplate.questionType] + " Question"}
                            childLabel={["answer", "answers"]}
                            arrayLength={answerOptionTemplates.length}
                        />
                        : ""
                    }

                    {/*// -===========================================================================================================*/}
                    {/*// -ANSWER EXPAND/COLLAPSE ALL ANSWERS BUTTON */}
                    {/*// -===========================================================================================================*/}

                    <div id="AnswerEditor-Answer-ExpandCollapseButton-container-div1" style={{display: "flex", flexDirection: "row", alignItems: "center", width: "100%"}}>
                        {(answerOptionTemplates && answerOptionTemplates.length > 0) &&

                        <div id="AnswerEditor-Answer-ExpandCollapseButton-container-div2" style={{display: "flex", marginLeft: "auto"}}>
                            <ArgoExpandCollapseButtonHook
                                fileName="AnswerEditor"
                                expandAll={expandAll}
                                onClick={expander.expandCollapseAll.bind(null, TEMPLATE.TYPE.ANSWER, expanderState, expandAll, Object.keys(expanderState), false, setStateCallback)}
                            />
                        </div>
                        }
                    </div>

                    {/*// -===========================================================================================================*/}
                    {/*// -ANSWER ACCORDION */}
                    {/*// -===========================================================================================================*/}

                    <div>&nbsp;</div>
                    {
                    answerOptionTemplates.map((answerOption, index) =>
                        <div key={"AnswerEditor-Accordion-container-div-" + index}>
                            <Accordion key={"AnswerEditor-Accordion" + index}
                                       id={"AnswerEditor-Accordion" + index}
                                       variant="outlined"
                                       disableGutters={true}
                                       expanded={isAccordionOpen(index) ?? false}
                                       onChange={expander.expandCollapseSelected.bind(null, expanderState, index, setStateCallback)}
                                       sx={{width: "100%", minWidth: 510}}>
                                <AccordionSummary
                                    id={"AnswerEditor-AccordionSummary-" + index}
                                    expandIcon={<ExpandMoreIcon style={{color: expander.ICON_COLOR}}/>}
                                    aria-controls="panel1-content"
                                    sx={{
                                        height: "30px",
                                        backgroundColor: expander.ACCORDION_BG_COLOR,
                                        '&:hover': {backgroundColor: expander.ACCORDION_BG_COLOR_HOVER},
                                        "& .MuiAccordionSummary-content": {
                                            alignItems: "center",
                                            display: "flex",
                                            gap: 1, // small spacing between groups
                                            minWidth: 0,
                                        }
                                    }}
                                >
                                    <Box style={{display: "flex", alignItems: "center", minWidth: 80}}>
                                        <ArgoReorderArrowsHook
                                            fileName="AnswerEditor"
                                            fieldName="container-div"
                                            onClick={onClickIconButtonReorder}
                                            index={index}
                                            arrayLength={arrayLength}
                                            isFirst={index === 0 || disableIfStateOrAddressQuestionType()}
                                            isLast={index === (answerOptionTemplates.length - 1) || disableIfStateOrAddressQuestionType()}
                                            upArrowColor={index === 0 ? expander.ICON_COLOR_HIDDEN : expander.ICON_COLOR}
                                            downArrowColor={index === (answerOptionTemplates.length - 1) ? expander.ICON_COLOR_HIDDEN : expander.ICON_COLOR}
                                        />
                                    </Box>

                                    <Box style={{display: "flex", alignItems: "center", minWidth: 118, flex: 1}}>
                                        <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 5,  minWidth: 118, color: expander.TEXT_COLOR_SECONDARY}}>Answer Text:</Typography>
                                        <Typography variant="h6" component="div"
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        paddingLeft: 10,
                                                        color: expander.TEXT_COLOR_PRIMARY,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}>
                                            {answerOption.optionText}
                                        </Typography>
                                    </Box>

                                    <Box style={{display: "flex", alignItems: "center", minWidth: 75}}>
                                        <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 10, minWidth: 75, color: expander.TEXT_COLOR_SECONDARY}}>Actions:</Typography>
                                        <Typography variant="subtitle1" component="div"
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        paddingLeft: 10,
                                                        color: expander.ICON_COLOR,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}>
                                            {"(" + answerOption.importActionKeys.length + ")"}
                                        </Typography>
                                    </Box>

                                    <Box style={{display: "flex", alignItems: "center", minWidth: 116}}>
                                        <Typography variant="h6" component="div" style={{flexShrink: 0, paddingLeft: 10, minWidth: 116, color: expander.TEXT_COLOR_SECONDARY}}>Show Group:</Typography>
                                        <Typography variant="subtitle1" component="div"
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        paddingLeft: 10,
                                                        color: expander.ICON_COLOR,
                                                        whiteSpace: "nowrap",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis"
                                                    }}>
                                            {"(" + (answerOption.importGroupKeys.length ? "Yes" : "No") + ")"}
                                        </Typography>
                                    </Box>

                                    {metadataUtils.countNumberOfVisibleMetadataEntries(answerOption.metadata) > 0 ?
                                        <Box style={{display: "flex", alignItems: "center", minWidth: 0}}>
                                            <Typography style={{flexShrink: 0, paddingLeft: 10,  color: expander.TEXT_COLOR_SECONDARY}}>Metadata:</Typography>
                                            <Typography variant="subtitle1" component="div"
                                                        style={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            paddingLeft: 10,
                                                            color: expander.ICON_COLOR,
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis"
                                                        }}>
                                                {"(" + metadataUtils.countNumberOfVisibleMetadataEntries(answerOption.metadata) + ")"}
                                            </Typography>
                                        </Box> : ""
                                    }

                                    <Box style={{display: "flex", alignItems: "center", marginLeft: "auto"}}>
                                        {(disableIfStateOrAddressQuestionType() || disableIfYesNoQuestionType()) ? "" :
                                            <ArgoIconButtonHook
                                                fileName="AnswerEditor"
                                                fieldName="remove"
                                                index={index}
                                                onClick={onClickIconButtonRemove}
                                                icon={<HighlightOffOutlinedIcon style={{color: expander.ICON_COLOR}}/>}
                                            />
                                        }
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <div>
                                        <AnswerEditorDetails
                                            template={template}
                                            index={index}
                                            answerOption={answerOption}
                                            answerOptionTemplates={answerOptionTemplates}
                                            questionDepth={questionDepth}
                                            displayAsLikert={displayAsLikert}
                                            parentSetStateCallback={parentSetStateCallback}
                                            answerEditorSetStateCallback={setStateCallback}
                                            groupTemplateMode={groupTemplateMode}
                                            setMetadataErrorText={setMetadataErrorText}
                                            showCustomColor={showCustomColor}
                                            createMode={createMode}
                                        />
                                    </div>
                                </AccordionDetails>
                            </Accordion>



                            {isAccordionOpen(index) ? <div>&nbsp;</div> : ""}
                        </div>
                    )}
                </div>
            : ""

    );
}

AnswerEditor.defaultProps = {
    disableRemoveIcon: false,
    isLikertAnswer: false,
    groupTemplateMode: false,
    showCustomColor: true
};

AnswerEditor.propTypes = {
    groupTemplateMode: PropTypes.bool,
    questionDepth: PropTypes.number,
    disableRemoveIcon: PropTypes.bool,
    isLikertAnswer: PropTypes.bool,
    displayAsLikert: PropTypes.bool,
    showCustomColor: PropTypes.bool,
    templateKey: PropTypes.string,
    hasAnswers: PropTypes.bool,
    template: PropTypes.object,
    answerOptionTemplates: PropTypes.array,
    setMetadataErrorText: PropTypes.func
};

export default AnswerEditor;